import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";
import EntitiesTable from "../shared/components/EntitiesTable";
import Pagination from "../shared/components/Pagination";
import useQueryParams from "customHooks/useQueryParams";
import { QUERY_PARAMS, USADMIN_ENTITIES } from "constants/constants";
import useDebounce from "customHooks/useDebounce";
import BaseError from "../shared/components/BaseError";
import { FilterOption } from "../shared/components/BaseFilters";
import { DEBOUNCE_TIME, ORDER_BY } from "../adminapp.constants";
import { extractFilters } from "../utils/extractFiltersFromSearchParams";
import { TERRITORY_MANAGER_TABLE } from "./territory-manager.constants";
import TerritoryManagerPageLayout from "./TerritoryManagerPageLayout";
import useMutateTerritoryManager from "./hooks/useMutateTerritoryManager";
import { TerritoryManager } from "./models/response/get-territory-managers-response";
import { useTerritoryManagers } from "./hooks/useTerritoryManagers";
import { getTerritoryManagerHeaders } from "./territory-manager-column-definition";
import DeletionModal from "../shared/components/DeletionModal";

const TerritoryManagersPage = () => {
  const intl = useIntl();
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const initialPage = parseInt(
    searchParams.get(QUERY_PARAMS.PAGE) || QUERY_PARAMS.INITIAL_PAGE,
    10
  );
  const initialSortBy =
    searchParams.get(QUERY_PARAMS.SORT_BY) || QUERY_PARAMS.BY_ID;
  const initialOrder =
    (searchParams.get(QUERY_PARAMS.ORDER_BY) as ORDER_BY) || ORDER_BY.DESC;
  const initialFilters = useMemo(
    () => extractFilters(searchParams),
    [searchParams]
  );
  const initialSearchQuery = searchParams.get(QUERY_PARAMS.SEARCH_QUERY) || "";

  const [currentPage, setCurrentPage] = useState(initialPage);
  const [sortBy, setSortBy] = useState<string>(initialSortBy);
  const [orderBy, setOrderBy] = useState<ORDER_BY>(initialOrder);
  const [filters, setFilters] =
    useState<Record<string, string>>(initialFilters);

  const [searchInput, setSearchInput] = useState<string>(initialSearchQuery);
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
  const debouncedSearchInput = useDebounce(searchInput, DEBOUNCE_TIME);

  useEffect(() => {
    setSearchQuery(debouncedSearchInput);
    setCurrentPage(1);
  }, [debouncedSearchInput]);

  const handleFilterChange = (
    mainFilter: FilterOption | null,
    subcategory: FilterOption | null
  ) => {
    if (mainFilter && subcategory) {
      setFilters((prev) => ({
        ...prev,
        [mainFilter.value]: subcategory.value,
      }));
      setCurrentPage(1);
    }
  };

  const handleRemoveFilter = (filterKey: string) => {
    setFilters((prev) => {
      const { [filterKey]: removed, ...newFilters } = prev;
      return newFilters;
    });

    setCurrentPage(1);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState("");
  const headers = getTerritoryManagerHeaders(intl);

  const queryParams = useQueryParams({
    currentPage,
    sortBy,
    orderBy,
    filters,
    searchQuery,
  });

  useEffect(() => {
    if (queryParams !== location.search.substring(1)) {
      history.replace({ search: "?" + queryParams });
    }
  }, [queryParams, history, location.search]);

  const { data, error, isLoading, isError, refetch } = useTerritoryManagers({
    currentPage,
    sortBy,
    orderBy,
    filters,
    searchQuery,
  });

  const totalPages = data?.meta?.totalPages || 1;

  const {
    deleteTerritoryManager,
    deleteTerritoryManagerIsError,
    deleteTerritoryManagerLoading,
    deleteTerritoryManagerReset,
    deleteTerritoryManagerSuccess,
  } = useMutateTerritoryManager({
    id: selectedEntityId as string,
  });

  const handleDelete = (entityId: string) => {
    const territoryManager = data?.data.find(
      (item) => item.id.toString() === entityId
    );
    const territoryManagerName = territoryManager?.name || "";
    setSelectedEntityId(entityId);
    setShowDeleteModal(true);
    setEntityName(territoryManagerName);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
  };

  const confirmDelete = () => {
    if (selectedEntityId) {
      deleteTerritoryManager({ territoryManagerId: selectedEntityId });
    }
    closeModal();
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setOrderBy(orderBy === ORDER_BY.ASC ? ORDER_BY.DESC : ORDER_BY.ASC);
    } else {
      setSortBy(field);
      setOrderBy(ORDER_BY.DESC);
    }
  };

  if (isError) {
    return <BaseError errorMessage={error} onRetry={refetch} />;
  }

  return (
    <TerritoryManagerPageLayout
      searchInput={searchInput}
      onSearchInputChange={setSearchInput}
      onFilterChange={handleFilterChange}
      appliedFilters={filters}
      onRemoveFilter={handleRemoveFilter}
    >
      <DeletionModal
        isOpen={
          showDeleteModal ||
          deleteTerritoryManagerIsError ||
          deleteTerritoryManagerSuccess
        }
        isError={deleteTerritoryManagerIsError}
        isSuccess={deleteTerritoryManagerSuccess}
        loading={deleteTerritoryManagerLoading}
        deletedEntityName={entityName}
        onClose={() => {
          setShowDeleteModal(false);
          deleteTerritoryManagerReset();
        }}
        onConfirm={confirmDelete}
      />
      <EntitiesTable<TerritoryManager>
        headers={headers}
        data={data?.data || []}
        entityType={USADMIN_ENTITIES.TERRITORY_MANAGERS}
        onDelete={handleDelete}
        sortBy={sortBy}
        order={orderBy}
        onSort={handleSort}
        loading={isLoading}
        loaderDimensions={{
          mobile: {
            rows: TERRITORY_MANAGER_TABLE.MOBILE_ROWS,
            columns: TERRITORY_MANAGER_TABLE.MOBILE_COLUMNS,
          },
          desktop: {
            rows: TERRITORY_MANAGER_TABLE.DESKTOP_ROWS,
            columns: TERRITORY_MANAGER_TABLE.DESKTOP_COLUMNS,
          },
        }}
      />
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          pageCount={totalPages}
          onPageChange={(page: number) => setCurrentPage(page)}
        />
      )}
    </TerritoryManagerPageLayout>
  );
};

export default TerritoryManagersPage;
